import { AxiosError } from "axios";
import { WheelEvent } from "react";

import { GemError } from "../Models/GemError";

/** Gets a mapping of a numerical typescript enum from its string keys to its numerical value.
 * @example for enum Test { x, y }, enumAsDictionary(Test) returns { x: 0, y: 1 }
 */
export const enumAsDictionary = <TEnum>(value: TEnum): Record<keyof TEnum, TEnum & number> => {
  const obj: Record<string, number> = {};
  const enumObject = value as unknown as Record<string, number>;
  Object.values(enumObject)
    .filter(e => e.constructor !== String)
    .map(
      enumValue =>
        (obj[
          enumObject[enumValue]
            .toString()
            .replace(/([A-Z])/g, " $1")
            .replace(/_/g, "-")
            .trim()
        ] = enumValue)
    );
  return obj as Record<keyof TEnum, TEnum & number>;
};

export const enumAsKVArray = <T>(value: unknown, sort?: boolean): { key: string; value: T }[] => {
  const array: { key: string; value: T }[] = [];
  const enumObject = value as Record<string, T>;
  const keys = Object.keys(enumObject);
  keys.slice(keys.length - keys.length / 2).forEach(keyValue =>
    array.push({
      key: keyValue
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, "-")
        .trim(),
      value: enumObject[keyValue]
    })
  );
  if (sort !== false) array.sort((a, b) => (a.key < b.key ? -1 : 1));
  return array;
};

export const missingObjectFailure = <T>(obj: T, throwMessage: string): T => {
  if (process.env.REACT_APP_ENVNAME !== "Production") throw throwMessage;
  else console.log("Missing Object Failure", "throwMessage");
  return obj;
};

const phoneRegex = /^\d{10}$/;
export const phoneIsValid = (phoneNumber?: string | null): boolean =>
  phoneNumber ? phoneRegex.test(phoneNumber) : false;
export const formatPhoneNumber = (phoneNumber?: string | null): string | null => {
  if (!phoneNumber) return null;
  if (phoneNumber.startsWith("+1")) return phoneNumber.substring(2);
  return phoneNumber;
};
export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const emailIsValid = (email?: string | null): boolean => (email ? emailRegex.test(email) : false);

const usaZipCodeRegEx = /^\d{5}$/;
const canadaZipCodeRegEx =
  /^([abceghj-nprstvxyABCEGHJ-NPRSTVXY]\d[abceghj-nprstv-zABCEGHJ-NPRSTV-Z])[ -]?(\d[abceghj-nprstv-zABCEGHJ-NPRSTV-Z]\d)$/;

export const zipCodeIsValid = (zipCode?: string | null): boolean =>
  zipCode ? usaZipCodeRegEx.test(zipCode) || canadaZipCodeRegEx.test(zipCode) : false;

// map an axios error into a telemetry message payload
export const buildError = (error: AxiosError<GemError>): Record<string, unknown> => {
  const { message, name, config, response } = error;
  return {
    request: {
      message,
      name,
      config
    },
    response: {
      headers: { "x-invocation-id": response?.headers["x-invocation-id"] },
      data: response?.data,
      status: response?.status,
      statusText: response?.statusText
    }
  };
};

/** Swallow wheel events to prevent incrementing a number field by un-focus/re-focusing the field. */
export const preventScrollIncrement = (e: WheelEvent<HTMLInputElement>) => {
  e.currentTarget.blur();
  e.stopPropagation();
  const target = e.currentTarget;
  setTimeout(() => target.focus(), 0);
};

/** Generates a random guid. */
export const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const groupBy = function <K extends PropertyKey, T>(
  items: T[],
  keySelector: (item: T, index: number) => K
): Record<K, T[]> {
  const map = new Map<K, T[]>();
  let index = 0;
  for (const item of items) {
    const key = keySelector(item, index++);
    const group = map.get(key);
    if (group) group.push(item);
    else map.set(key, [item]);
  }
  return Object.fromEntries(map) as Record<K, T[]>;
};
